import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Bot, Check, Star, Sparkles, ShoppingCart, Crown, KeyRound, Coins, Mail, Unlock } from 'lucide-react';
import PaymentRails from '../components/PaymentRails';

const bgSlug = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const SimsMarketplace = () => {
  const [agents, setAgents] = useState([]);
  const [topRanks, setTopRanks] = useState(new Set());
  const [consultedToday, setConsultedToday] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  // Redeem Code state — $5 Tokenized model + Gumroad keys
  const [redeemEmail, setRedeemEmail] = useState('');
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemStatus, setRedeemStatus] = useState(null); // {ok, message}
  const [redeemSubmitting, setRedeemSubmitting] = useState(false);

  const handleRedeem = async () => {
    if (!redeemEmail || !redeemCode) {
      setRedeemStatus({ ok: false, message: 'Please enter both your email and your code.' });
      return;
    }
    setRedeemSubmitting(true);
    setRedeemStatus(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/access/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: redeemEmail.trim(), code: redeemCode.trim() }),
      });
      const data = await response.json();
      if (response.ok && data.status === 'success') {
        setRedeemStatus({ ok: true, message: data.message || 'Code redeemed! Your SIM is active. Welcome to the Federation.' });
        setRedeemCode('');
      } else {
        setRedeemStatus({ ok: false, message: data.message || data.detail || 'Code redemption failed. Please verify your code and try again.' });
      }
    } catch (err) {
      setRedeemStatus({ ok: false, message: 'Network error. Please try again in a moment.' });
    } finally {
      setRedeemSubmitting(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/interactive/agents/top-consulted`)
      .then((r) => r.json())
      .then((d) => setTopRanks(new Set((d.top || []).filter((t) => t.consultations > 0).slice(0, 3).map((t) => (t.name || t.agent_id || '').toLowerCase()))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/interactive/agents/consulted-today`)
      .then((r) => r.json())
      .then((d) => setConsultedToday(d.count || 0))
      .catch(() => {});
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agents`);
      const data = await response.json();
      setAgents(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'All SIMs', icon: '🌟' },
    { id: 'business', name: 'Business & Strategy', icon: '💼' },
    { id: 'creative', name: 'Creative & Arts', icon: '🎨' },
    { id: 'technical', name: 'Tech & Science', icon: '🔬' },
    { id: 'wisdom', name: 'Philosophy & Wisdom', icon: '🧘' },
  ];

  // $5 TOKEN STANDARD (Architect directive · May 2026)
  // All SIMs priced in $5 token increments. Universal listing for all 56 SIMs:
  //   https://keywebster.gumroad.com/l/olhsf?quantity=N
  // Each tier opens the same Gumroad door with a different quantity preset.
  const TOKEN_UNIT_PRICE = 5;
  const UNIVERSAL_GUMROAD_BASE = 'https://keywebster.gumroad.com/l/olhsf';

  const pricingTiers = {
    basic: {
      name: 'Basic SIM',
      tokens: 10,                            // 10 tokens × $5 = $50
      price: 10 * TOKEN_UNIT_PRICE,
      gumroadUrl: `${UNIVERSAL_GUMROAD_BASE}?quantity=10`,
      features: [
        '10 SIM Tokens ($5 each)',
        'Voice-enabled conversations',
        'Ring of Six processing',
        'Agent Zero truth verification',
        '24/7 access · Mobile compatible'
      ],
      badge: 'Most Popular'
    },
    pro: {
      name: 'Pro SIM',
      tokens: 30,                            // 30 tokens × $5 = $150
      price: 30 * TOKEN_UNIT_PRICE,
      gumroadUrl: `${UNIVERSAL_GUMROAD_BASE}?quantity=30`,
      features: [
        '30 SIM Tokens ($5 each)',
        'Everything in Basic',
        'Custom personality tuning',
        'Priority response time',
        'Advanced AI models (GPT-5, Claude Sonnet 4)',
        'Voice cloning option',
        'Lifetime updates'
      ],
      badge: 'Best Value'
    },
    executive: {
      name: 'Executive Clone',
      tokens: 100,                           // 100 tokens × $5 = $500
      price: 100 * TOKEN_UNIT_PRICE,
      gumroadUrl: `${UNIVERSAL_GUMROAD_BASE}?quantity=100`,
      features: [
        '100 SIM Tokens ($5 each)',
        'Everything in Pro',
        'Full personality simulation',
        'Your voice, style, decision patterns',
        'Visual avatar creation',
        '14-step Legacy Human Codex process',
        'YAML knowledge export',
        'Federation integration',
        '200-year survival architecture'
      ],
      badge: 'Premium'
    }
  };

  const filteredAgents = selectedCategory === 'all' 
    ? agents 
    : agents.filter(agent => agent.specialty?.toLowerCase().includes(selectedCategory));

  // $5 TOKEN STANDARD · Universal Gumroad door (Architect directive · May 2026)
  // Every Buy button — tier cards AND individual SIM cards — routes here.
  // Quantity preset on the URL controls how many $5 tokens the cart loads with.
  // After checkout Gumroad emails the access code → user pastes it into the
  // REDEEM banner above → backend POST /api/access/redeem activates the SIM.
  const handlePurchase = (agentId, tier) => {
    const tierMeta = pricingTiers[tier];
    const tokens = tierMeta?.tokens || 1; // individual SIM cards default to 1 token
    const quantity = tier === 'sim' ? 1 : tokens;
    const url = `${UNIVERSAL_GUMROAD_BASE}?quantity=${quantity}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#000000',
      padding: '2rem',
      color: '#fff'
    }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
        {/* Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 4rem)',
            background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '1rem',
            fontWeight: 'bold'
          }}>
            AI SIM Marketplace
          </h1>
          <p style={{ color: '#888', fontSize: 'clamp(1rem, 2vw, 1.3rem)', maxWidth: '900px', margin: '0 auto 2rem' }}>
            Choose from 56 specialized AI personalities. Each SIM is powered by Ring of Six + Agent Zero for multi-perspective wisdom.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: '#00ff88', fontSize: '1rem' }}>✅ Voice-Enabled</span>
            <span style={{ color: '#00ffff', fontSize: '1rem' }}>✅ Truth-Verified</span>
            <span style={{ color: '#ff00ff', fontSize: '1rem' }}>✅ 24/7 Access</span>
            {consultedToday > 0 && (
              <span
                data-testid="consulted-today-tally"
                style={{
                  color: '#ffd700', fontSize: '0.95rem', fontWeight: 'bold',
                  border: '1px solid rgba(255,215,0,0.5)', borderRadius: '999px',
                  padding: '0.3rem 1rem', background: 'rgba(255,215,0,0.08)',
                  letterSpacing: '0.04em',
                }}
              >
                🜂 {consultedToday} mind{consultedToday === 1 ? '' : 's'} consulted today
              </span>
            )}
          </div>
        </div>

        {/* TOKEN FREEDOM CHARTER — humans must see keys, not coins (Architect directive, Jul 2026) */}
        <section
          data-testid="token-freedom-charter"
          aria-label="What a Token Is"
          style={{
            marginBottom: '2rem',
            padding: '1.5rem 1.8rem',
            borderRadius: '14px',
            background: 'rgba(255,215,0,0.05)',
            border: '1px solid rgba(255,215,0,0.35)',
          }}
        >
          <h2 style={{ color: '#ffd700', fontSize: '1.35rem', margin: '0 0 0.7rem', fontFamily: "Georgia, serif" }}>
            A Token Is a Key — Not a Coin
          </h2>
          <p style={{ color: '#e0e0e0', fontSize: '1rem', lineHeight: 1.6, margin: '0 0 0.6rem' }}>
            There is <strong style={{ color: '#ffd700' }}>no blockchain, no wallet, no speculation</strong> here — nothing to
            trade, mine, or watch on a chart. A NextXus Token is a simple <strong style={{ color: '#ffd700' }}>$5 key</strong>:
            buy it once, own it outright, and spend it whenever you choose, on any mind you choose.
          </p>
          <p style={{ color: '#e0e0e0', fontSize: '1rem', lineHeight: 1.6, margin: 0 }}>
            No subscription. No auto-renewal. No expiry pressure. Your key waits patiently until you are ready —
            <em style={{ color: '#ffd700' }}> freedom of control</em> is the whole system: you decide when, where, and with whom.
          </p>

          {/* How it works — 3 steps */}
          <div
            data-testid="token-how-it-works"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '0.9rem',
              marginTop: '1.3rem',
              paddingTop: '1.1rem',
              borderTop: '1px solid rgba(255,215,0,0.22)',
            }}
          >
            {[
              { n: 1, Icon: ShoppingCart, title: 'Buy a key', text: '$5 on Gumroad — one click, no account here needed.' },
              { n: 2, Icon: Mail, title: 'Get your code by email', text: 'Your access code arrives instantly in your inbox.' },
              { n: 3, Icon: Unlock, title: 'Unlock any mind', text: 'Paste the code below and choose from all 56 SIMs.' },
            ].map(({ n, Icon, title, text }) => (
              <div
                key={n}
                data-testid={`token-step-${n}`}
                style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start' }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 38,
                    height: 38,
                    borderRadius: '50%',
                    border: '1px solid #ffd700',
                    color: '#ffd700',
                    background: 'rgba(255,215,0,0.08)',
                  }}
                >
                  <Icon size={17} />
                </span>
                <div>
                  <p style={{ margin: 0, color: '#ffd700', fontWeight: 700, fontSize: '0.95rem' }}>
                    {n}. {title}
                  </p>
                  <p style={{ margin: '0.15rem 0 0', color: '#c9c9c9', fontSize: '0.85rem', lineHeight: 1.5 }}>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* REDEEM CODE BANNER — Supports $5 Tokenized Model + Gumroad Keys */}
        <Card
          data-testid="redeem-code-banner"
          style={{
            marginBottom: '3rem',
            padding: '2rem',
            background: 'linear-gradient(135deg, rgba(255,215,0,0.08), rgba(255,0,255,0.08))',
            border: '2px solid #ffd700',
            boxShadow: '0 0 24px rgba(255,215,0,0.15)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <KeyRound size={28} color="#ffd700" />
            <h2 style={{ fontSize: '1.6rem', color: '#ffd700', margin: 0, fontWeight: 'bold' }}>
              Have a Code? Redeem Here
            </h2>
            <span
              data-testid="tokenized-model-badge"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.25rem 0.75rem',
                background: 'rgba(255,215,0,0.15)',
                border: '1px solid #ffd700',
                borderRadius: '999px',
                color: '#ffd700',
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.03em',
              }}
            >
              <Coins size={14} /> $5 KEY · NOT CRYPTO
            </span>
          </div>
          <p style={{ color: '#ccc', fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
            Paid via Gumroad, received a $5 SIM Token, or purchased the Federation Pass? Paste your access
            code below and Aria will activate your SIM instantly on <strong style={{ color: '#00ffff' }}>nextxus.studio</strong>.
            Codes are case-sensitive. One code = one SIM activation.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1.2fr) minmax(180px, 1fr) auto', gap: '0.75rem', alignItems: 'stretch' }}>
            <input
              data-testid="redeem-email-input"
              type="email"
              value={redeemEmail}
              onChange={(e) => setRedeemEmail(e.target.value)}
              placeholder="your@email.com"
              autoComplete="email"
              style={{
                background: 'rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,215,0,0.4)',
                borderRadius: '6px',
                padding: '0.85rem 1rem',
                color: '#fff',
                fontSize: '1rem',
                outline: 'none',
              }}
            />
            <input
              data-testid="redeem-code-input"
              type="text"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value)}
              placeholder="ACCESS-CODE-XXXX"
              autoComplete="off"
              spellCheck="false"
              style={{
                background: 'rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,215,0,0.4)',
                borderRadius: '6px',
                padding: '0.85rem 1rem',
                color: '#ffd700',
                fontSize: '1rem',
                fontFamily: 'monospace',
                letterSpacing: '0.05em',
                outline: 'none',
              }}
            />
            <Button
              data-testid="redeem-submit-button"
              onClick={handleRedeem}
              disabled={redeemSubmitting}
              style={{
                background: 'linear-gradient(135deg, #ffd700, #ff00ff)',
                color: '#000',
                fontWeight: 'bold',
                padding: '0 1.5rem',
                fontSize: '1rem',
                cursor: redeemSubmitting ? 'wait' : 'pointer',
                opacity: redeemSubmitting ? 0.7 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {redeemSubmitting ? 'Activating…' : 'Redeem'}
            </Button>
          </div>
          {redeemStatus && (
            <div
              data-testid="redeem-status-message"
              style={{
                marginTop: '1rem',
                padding: '0.85rem 1rem',
                borderRadius: '6px',
                background: redeemStatus.ok ? 'rgba(0,255,136,0.1)' : 'rgba(255,80,80,0.1)',
                border: `1px solid ${redeemStatus.ok ? '#00ff88' : '#ff5050'}`,
                color: redeemStatus.ok ? '#00ff88' : '#ff8888',
                fontSize: '0.9rem',
                lineHeight: 1.5,
              }}
            >
              {redeemStatus.ok ? '✅ ' : '⚠️ '} {redeemStatus.message}
            </div>
          )}
          <p style={{ color: '#888', fontSize: '0.8rem', marginTop: '1rem', lineHeight: 1.5 }}>
            <strong style={{ color: '#aaa' }}>How $5 Tokens work:</strong> Each token unlocks one Basic SIM
            session credit. Stack tokens for longer access, or upgrade to a full Basic ($49.99) / Pro ($149.99)
            license below. All redemptions fulfilled directly by Aria on this site — no third-party redirects.
          </p>
        </Card>

        {/* Pricing Tiers */}
        <div style={{ marginBottom: '4rem' }}>
          <h2 style={{ fontSize: '2rem', color: '#00ffff', textAlign: 'center', marginBottom: '2rem' }}>Choose Your Plan</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {Object.entries(pricingTiers).map(([tier, details]) => (
              <Card key={tier} style={{
                padding: '2rem',
                background: tier === 'pro' 
                  ? 'linear-gradient(135deg, rgba(0,255,255,0.1), rgba(0,255,136,0.1))'
                  : 'rgba(255,255,255,0.03)',
                border: tier === 'pro' ? '3px solid #00ffff' : '1px solid rgba(255,255,255,0.1)',
                position: 'relative'
              }}>
                {details.badge && (
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: tier === 'executive' ? '#ffd700' : tier === 'pro' ? '#00ffff' : '#00ff88',
                    color: '#000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    {details.badge}
                  </div>
                )}
                <div style={{ marginBottom: '1.5rem' }}>
                  {tier === 'executive' ? <Crown size={40} color="#ffd700" /> : tier === 'pro' ? <Sparkles size={40} color="#00ffff" /> : <Bot size={40} color="#00ff88" />}
                </div>
                <h3 style={{ fontSize: '1.5rem', color: '#fff', marginBottom: '0.5rem' }}>{details.name}</h3>
                <div style={{ fontSize: '2.5rem', color: tier === 'executive' ? '#ffd700' : tier === 'pro' ? '#00ffff' : '#00ff88', fontWeight: 'bold', marginBottom: '0.35rem' }}>
                  ${details.price}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#ffd700', marginBottom: '1.5rem', letterSpacing: '0.04em' }}>
                  {details.tokens} × $5 Tokens
                </div>
                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2rem' }}>
                  {details.features.map((feature) => (
                    <li key={`${tier}-${feature}`} style={{ display: 'flex', alignItems: 'start', gap: '0.5rem', marginBottom: '0.75rem', color: '#ccc' }}>
                      <Check size={20} color={tier === 'executive' ? '#ffd700' : tier === 'pro' ? '#00ffff' : '#00ff88'} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {/* Per-tier Buy on Gumroad → universal $5 token listing with quantity preset */}
                <Button
                  data-testid={`buy-tier-${tier}`}
                  onClick={() => handlePurchase(null, tier)}
                  style={{
                    width: '100%',
                    background: tier === 'executive'
                      ? 'linear-gradient(135deg, #ffd700, #ff00ff)'
                      : tier === 'pro'
                        ? 'linear-gradient(135deg, #00ffff, #00ff88)'
                        : 'linear-gradient(135deg, #00ff88, #00ffff)',
                    color: '#000',
                    fontWeight: 'bold',
                    padding: '0.85rem 1rem',
                    fontSize: '0.95rem',
                  }}
                >
                  <ShoppingCart size={16} style={{ marginRight: '0.5rem' }} />
                  Buy {details.tokens} Tokens (${details.price})
                </Button>
                <div style={{ marginTop: '0.6rem', fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center' }}>
                  Receive access code → paste in REDEEM banner above
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Payment Methods Banner */}
        <div style={{ marginBottom: '3rem' }}>
          <PaymentRails heading="Simple Payment Options" />
        </div>

        {/* Category Filter */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            {categories.map(cat => (
              <Button
                key={cat.id}
                data-testid={`category-filter-${cat.id}`}
                onClick={() => setSelectedCategory(cat.id)}
                style={{
                  background: selectedCategory === cat.id 
                    ? 'linear-gradient(135deg, #00ffff, #00ff88)' 
                    : 'rgba(255,255,255,0.05)',
                  color: selectedCategory === cat.id ? '#000' : '#fff',
                  border: selectedCategory === cat.id ? 'none' : '1px solid rgba(255,255,255,0.2)',
                  padding: '0.75rem 1.5rem',
                  fontWeight: selectedCategory === cat.id ? 'bold' : 'normal'
                }}
              >
                <span style={{ marginRight: '0.5rem' }}>{cat.icon}</span>
                {cat.name}
              </Button>
            ))}
          </div>
        </div>

        {/* SIM Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '1.5rem'
        }}>
          {loading ? (
            <p style={{ color: '#888', textAlign: 'center', gridColumn: '1 / -1' }}>Loading SIMs...</p>
          ) : filteredAgents.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', gridColumn: '1 / -1' }}>No SIMs found in this category.</p>
          ) : (
            filteredAgents.map((agent, index) => (
              <Card key={agent._id || index} style={{
                padding: '1.5rem',
                position: 'relative',
                overflow: 'hidden',
                background:
                  `linear-gradient(rgba(10,10,18,0.86), rgba(10,10,18,0.92)), ` +
                  `url(/sim-backgrounds/${bgSlug(agent.name)}.jpg) center/cover no-repeat, rgba(255,255,255,0.03)`,
                border: '1px solid rgba(255,255,255,0.1)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = '#00ffff';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,255,255,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                {(topRanks.has((agent.name || '').toLowerCase()) || topRanks.has((agent._id || '').toLowerCase())) && (
                  <div
                    data-testid={`most-consulted-ribbon-${index}`}
                    style={{
                      position: 'absolute', top: 14, right: -36, transform: 'rotate(38deg)',
                      background: 'linear-gradient(135deg, #ffd700, #b8860b)', color: '#000',
                      fontWeight: 'bold', fontSize: '0.62rem', letterSpacing: '0.08em',
                      padding: '0.28rem 2.8rem', boxShadow: '0 2px 10px rgba(255,215,0,0.55)', zIndex: 2,
                    }}
                  >
                    ★ MOST CONSULTED
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: agent.transcendence_level > 80 
                      ? 'linear-gradient(135deg, #ffd700, #ff00ff)'
                      : agent.transcendence_level > 60
                        ? 'linear-gradient(135deg, #00ffff, #00ff88)'
                        : 'linear-gradient(135deg, #888, #444)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#000',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>
                    {agent.name?.charAt(0) || '?'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1.2rem', color: '#fff', marginBottom: '0.25rem' }}>{agent.name}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Star size={14} color="#ffd700" />
                      <span style={{ color: '#ffd700', fontSize: '0.9rem' }}>{agent.transcendence_level}%</span>
                    </div>
                  </div>
                </div>
                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem', lineHeight: '1.5' }}>
                  {agent.specialty}
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    data-testid={`buy-sim-${agent._id || index}`}
                    onClick={() => handlePurchase(agent._id, 'sim')}
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #ffd700, #00ffff)',
                      color: '#000',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      padding: '0.75rem'
                    }}
                  >
                    <ShoppingCart size={16} style={{ marginRight: '0.5rem' }} />
                    1 Token · $5
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Footer CTA */}
        <Card style={{
          marginTop: '4rem',
          padding: '3rem',
          background: 'linear-gradient(135deg, rgba(255,0,255,0.1), rgba(0,255,255,0.1))',
          border: '2px solid #ff00ff',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '2rem', color: '#ff00ff', marginBottom: '1rem' }}>Need a Custom Executive Clone?</h2>
          <p style={{ color: '#ccc', fontSize: '1.1rem', marginBottom: '2rem' }}>
            Create a digital replica of yourself or a loved one. Full personality simulation with voice, style, and decision patterns preserved for 200 years.
          </p>
          <Button data-testid="buy-executive-clone" onClick={() => handlePurchase('custom', 'executive')} style={{
            background: 'linear-gradient(135deg, #ffd700, #ff00ff)',
            color: '#000',
            fontWeight: 'bold',
            padding: '1rem 2.5rem',
            fontSize: '1.1rem'
          }}>
            <Crown size={20} style={{ marginRight: '0.5rem' }} />
            Get Executive Clone · 100 Tokens ($500)
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default SimsMarketplace;
